import type { ComponentType, CSSProperties, ReactNode } from "react"
import { ArrowLeft, ChevronRight, Info } from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * Briques visuelles partagées par les trois écrans du Profil (Profil, Mon programme,
 * Réglages) et l'écran d'historique d'un record.
 *
 * Elles fixent la direction visuelle du lot 1 en un seul endroit :
 *   — en-tête de section : tiret dégradé + petites capitales espacées (motif DashboardHome) ;
 *   — carte : coins très arrondis, dégradé léger vers le haut, liseré lumineux sur le bord
 *     supérieur — et ENTIÈREMENT cliquable quand elle l'est, jamais un bouton posé dedans ;
 *   — code couleur : cyan pour les données du client, ambre pour les records, violet pour
 *     les mesures corporelles, rouge réservé au destructif.
 */

// ── Code couleur ────────────────────────────────────────────────────────────
export type Tone = "cyan" | "amber" | "violet" | "slate" | "red"

export const TONE: Record<Tone, { fg: string; bg: string; border: string }> = {
  cyan:   { fg: "#06b6d4", bg: "rgba(6,182,212,0.10)",   border: "rgba(6,182,212,0.20)" },
  amber:  { fg: "#f59e0b", bg: "rgba(245,158,11,0.10)",  border: "rgba(245,158,11,0.20)" },
  violet: { fg: "#8b5cf6", bg: "rgba(139,92,246,0.10)",  border: "rgba(139,92,246,0.20)" },
  slate:  { fg: "#94a3b8", bg: "rgba(148,163,184,0.10)", border: "rgba(148,163,184,0.18)" },
  red:    { fg: "#ef4444", bg: "rgba(239,68,68,0.10)",   border: "rgba(239,68,68,0.20)" },
}

// ── En-tête de section ──────────────────────────────────────────────────────
export function SectionTitle({ label, right }: { label: string; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-3 ml-1">
      <div className="flex items-center gap-2">
        <span
          className="w-[16px] h-[2px] rounded-full shrink-0"
          style={{ background: "linear-gradient(90deg, #06b6d4, transparent)" }}
        />
        <span className="text-[10px] font-bold text-gray-300 uppercase" style={{ letterSpacing: 2 }}>
          {label}
        </span>
      </div>
      {right}
    </div>
  )
}

/** Métadonnée à droite d'un en-tête de section (date de relevé, unité…). */
export function SectionMeta({ children }: { children: ReactNode }) {
  return <span className="text-[10px] text-gray-600 tnum">{children}</span>
}

/**
 * Ligne de contexte sous un en-tête de section : de quand date la valeur affichée, quand
 * la suivante est attendue. C'est un repère qu'on lit quand on le cherche, pas une alerte.
 *
 * Quatre écarts la séparent des sous-titres de groupe qui peuvent la suivre
 * (« COMPOSITION », « MENSURATIONS ») : plus petite (9 px contre 10), un gris plus atténué
 * (gray-700 contre gray-600), la casse normale contre des petites capitales espacées, et un
 * vrai blanc derrière. Sans ça les deux se confondaient — même corps, même gris — et on ne
 * distinguait plus le titre du groupe de la note de contexte. C'est bien la note qui recule :
 * les sous-titres gardent le gris commun à toute l'app (« MON BLOC », « RECORDS »…).
 *
 * Le `-mt-1.5` la recolle au titre de section auquel elle appartient, le `mb-5` la détache
 * de ce qui suit : elle se rattache visuellement vers le haut.
 */
export function SectionCaption({ children }: { children: ReactNode }) {
  return <p className="-mt-1.5 mb-5 ml-1 text-[9px] leading-relaxed text-gray-700 tnum">{children}</p>
}

// ── Carte ───────────────────────────────────────────────────────────────────
const CARD_SURFACE: CSSProperties = {
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.032), rgba(255,255,255,0) 42%), rgba(13,20,32,0.92)",
  border: "1px solid rgba(30,41,59,0.55)",
}

/** Liseré lumineux sur le bord supérieur — purement décoratif. */
function CardSeam() {
  return (
    <span
      aria-hidden
      className="absolute top-0 left-[16%] right-[16%] h-px pointer-events-none"
      style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.16), transparent)" }}
    />
  )
}

/**
 * Carte non cliquable. Pour une carte cliquable, utiliser `CardButton` : la carte entière
 * porte l'action, on ne pose pas de bouton à l'intérieur d'une carte.
 */
export function Card({
  children, className, style,
}: { children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <div className={cn("relative rounded-[22px] overflow-hidden", className)} style={{ ...CARD_SURFACE, ...style }}>
      <CardSeam />
      {children}
    </div>
  )
}

export function CardButton({
  children, onClick, className, style, disabled,
}: {
  children: ReactNode
  onClick: () => void
  className?: string
  style?: CSSProperties
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "relative w-full text-left rounded-[22px] overflow-hidden transition-all",
        disabled ? "cursor-default opacity-60" : "cursor-pointer active:scale-[0.99]",
        className
      )}
      style={{ ...CARD_SURFACE, ...style }}
    >
      <CardSeam />
      {children}
    </button>
  )
}

// ── Lignes de liste ─────────────────────────────────────────────────────────
export interface RowProps {
  icon?: ComponentType<{ className?: string }>
  /**
   * Illustration en couleur (fichier de /illustrations) à la place de `icon`, dans la
   * même pastille : même encombrement, la hauteur et l'alignement des lignes ne bougent pas.
   */
  illustration?: string
  tone?: Tone
  label: string
  sublabel?: string
  value?: ReactNode
  /** Rendu à droite à la place de `value` + chevron (interrupteur, badge…). */
  right?: ReactNode
  onClick?: () => void
  /**
   * La ligne ouvre une INFORMATION, pas un éditeur : icône ⓘ au lieu du chevron, pour ne
   * pas avoir l'air modifiable (ex. « Jours » de Mon programme, lot 3).
   */
  info?: boolean
  danger?: boolean
  last?: boolean
}

export function Row({ icon: Icon, illustration, tone = "slate", label, sublabel, value, right, onClick, info, danger, last }: RowProps) {
  const t = TONE[danger ? "red" : tone]
  const interactive = Boolean(onClick)
  const border = last ? "none" : "1px solid rgba(30,41,59,0.35)"

  const inner = (
    <>
      {(Icon || illustration) && (
        <span
          className="w-[34px] h-[34px] rounded-[11px] flex items-center justify-center shrink-0"
          style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.fg }}
        >
          {illustration
            ? <img src={illustration} alt="" aria-hidden className="w-6 h-6" />
            : Icon && <Icon className="w-4 h-4" />}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-medium truncate" style={{ color: danger ? "#f87171" : "#e2e8f0" }}>
          {label}
        </span>
        {sublabel && <span className="block text-[11.5px] text-gray-500 mt-0.5">{sublabel}</span>}
      </span>
      {right ?? (
        <>
          {value !== undefined && (
            <span className="text-[13.5px] font-medium text-gray-400 text-right truncate max-w-[46%] tnum">{value}</span>
          )}
          {interactive && (info
            ? <Info className="w-4 h-4 text-gray-600 shrink-0" aria-label="Information" />
            : <ChevronRight className="w-4 h-4 text-gray-700 shrink-0" />)}
        </>
      )}
    </>
  )

  const className = "w-full flex items-center gap-3.5 px-4 py-3.5 text-left min-h-[56px] transition-all"

  if (interactive) {
    return (
      <button type="button" onClick={onClick} className={cn(className, "cursor-pointer hover:bg-white/[0.02] active:scale-[0.995]")} style={{ borderBottom: border }}>
        {inner}
      </button>
    )
  }
  return (
    <div className={cn(className, "cursor-default")} style={{ borderBottom: border }}>
      {inner}
    </div>
  )
}

/** Groupe de lignes dans une carte. */
export function RowList({ children }: { children: ReactNode }) {
  return <Card>{children}</Card>
}

// ── Interrupteur ────────────────────────────────────────────────────────────
export function Toggle({
  value, onChange, disabled, label,
}: {
  value: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!value)}
      className={cn(
        "relative w-11 h-6 rounded-full transition-colors shrink-0",
        disabled ? "cursor-default opacity-40" : "cursor-pointer"
      )}
      style={{ background: value ? "#06b6d4" : "rgba(71,85,105,0.6)" }}
    >
      <span
        className="absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white shadow transition-all duration-200"
        style={{ left: value ? "22px" : "3px" }}
      />
    </button>
  )
}

// ── Note sous un groupe ─────────────────────────────────────────────────────
export function Note({ children }: { children: ReactNode }) {
  return <p className="text-[11.5px] text-gray-500 leading-relaxed mt-2.5 mx-1">{children}</p>
}

// ── En-tête d'écran secondaire ──────────────────────────────────────────────
export function ScreenHeader({ title, onBack, right }: { title: string; onBack: () => void; right?: ReactNode }) {
  return (
    <div
      className="sticky top-0 z-30 px-4 pt-5 pb-3 flex items-center gap-2"
      style={{
        background: "linear-gradient(to bottom, rgba(6,10,19,0.97), rgba(6,10,19,0.78) 65%, transparent)",
        backdropFilter: "blur(16px)",
      }}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label="Retour"
        className="w-9 h-9 rounded-[11px] bg-gray-800/50 flex items-center justify-center hover:bg-gray-700/50 transition-colors cursor-pointer shrink-0"
      >
        <ArrowLeft className="w-[18px] h-[18px] text-gray-400" />
      </button>
      <h1 className="flex-1 text-center text-[16px] font-semibold text-white tracking-tight truncate">{title}</h1>
      <span className="w-9 shrink-0 flex justify-end">{right}</span>
    </div>
  )
}
